import { useEffect, useMemo, useRef, useState } from "react";

import { MedicamentoCombobox } from "@/components/MedicamentoCombobox";
import { PacienteFormModal } from "@/components/PacienteFormModal";

import { PrescricaoCamera } from "@/components/PrescricaoCamera";
import { RoleGate } from "@/components/RoleGate";
import { Scanner } from "@/components/Scanner";
import {
  abrirPrescricao,
  lookupLotePorEtiqueta,
  lookupMedicamentoPorEan,
  useAnexarPrescricao,
  useAnularDispensacao,
  useCriarDispensacao,
  useCriarDispensacaoEnfermagem,
  useCriarMedico,
  useLerPrescricao,
  useMedicos,
  useLiberarDispensacao,
  useRecusarDispensacao,
  useDispensacoes,
  useFefoSugestao,
  useLocais,
  useMedicamentos,
  usePacientes,
} from "@/hooks/useApi";
import { useUser } from "@/hooks/useUser";
import type { Dispensacao, Medicamento, Medico, Paciente } from "@/types/api";

const RECEITUARIOS_RETIDOS = new Set(["A1", "A2", "A3", "B1", "B2"]);

function fmtCpf(cpf: string): string {
  if (cpf.length !== 11) return cpf;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  return s.split("-").reverse().join("/");
}

export function DispensacoesPage() {
  return (
    <RoleGate allowed={["admin", "auditor", "farmaceutico", "enfermeira_chefe", "enfermagem"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const { data: user } = useUser();
  const { data: dispensacoes, isLoading } = useDispensacoes();
  const { data: locais } = useLocais();
  const [showNova, setShowNova] = useState(false);

  const podeDispensar =
    user && ["admin", "farmaceutico", "enfermeira_chefe", "enfermagem"].includes(user.papel);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Dispensações ao paciente</h2>
          <p className="text-sm text-ink-muted">
            Saída final ao paciente. Alto custo e Portaria 344 exigem médico prescritor + CRM.
          </p>
        </div>
        {podeDispensar ? (
          <button className="btn-primary" onClick={() => setShowNova((v) => !v)}>
            {showNova ? "Fechar" : "Nova dispensação"}
          </button>
        ) : null}
      </div>

      {showNova && locais ? <NovaDispensacao onDone={() => setShowNova(false)} /> : null}

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {dispensacoes && dispensacoes.length > 0 ? (
        <div className="space-y-3">
          {dispensacoes.map((d) => <DispCard key={d.id} d={d} />)}
        </div>
      ) : dispensacoes && dispensacoes.length === 0 ? (
        <p className="text-sm text-ink-muted">Sem dispensações registradas.</p>
      ) : null}
    </div>
  );
}

function ModalAnular({ d, onClose }: { d: Dispensacao; onClose: () => void }) {
  const anular = useAnularDispensacao();
  const [motivo, setMotivo] = useState("");
  const ok = motivo.trim().length >= 5;

  async function confirmar() {
    if (!ok) return;
    try {
      await anular.mutateAsync({ id: d.id, motivo: motivo.trim() });
      onClose();
    } catch { /* erro via anular.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">Anular dispensação</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>
        <div className="rounded bg-gray-50 px-3 py-2 text-sm">
          <p><b className="font-mono">{d.numero}</b> · {d.paciente_nome}</p>
          <p className="text-xs text-ink-muted mt-0.5">
            {d.itens.length} item(ns) · dispensado em {fmtDateTime(d.created_at)}
          </p>
        </div>
        <p className="text-xs text-ink-muted">
          A anulação <b>não apaga</b> a dispensação: devolve os itens ao estoque de origem,
          reverte o livro de controlados (se houver) e mantém o registro marcado como
          anulado, com este motivo — visível em auditoria e relatórios (rastreabilidade ANVISA).
        </p>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">
            Motivo <span className="text-anarosa">(obrigatório)</span>
          </label>
          <input
            className="input mt-1"
            placeholder="ex.: utilizada como treinamento, sem fundamento assistencial"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!ok || anular.isPending} onClick={confirmar}>
            {anular.isPending ? "Anulando…" : "Confirmar anulação"}
          </button>
        </div>
        {anular.error ? (
          <p className="text-sm text-anarosa">{(anular.error as Error).message}</p>
        ) : null}
      </div>
    </div>
  );
}

function ModalRecusar({ d, onClose }: { d: Dispensacao; onClose: () => void }) {
  const recusar = useRecusarDispensacao();
  const [motivo, setMotivo] = useState("");
  const ok = motivo.trim().length >= 5;

  async function confirmar() {
    if (!ok) return;
    try {
      await recusar.mutateAsync({ id: d.id, motivo: motivo.trim() });
      onClose();
    } catch { /* erro via recusar.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">Recusar solicitação</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>
        <div className="rounded bg-gray-50 px-3 py-2 text-sm">
          <p><b className="font-mono">{d.numero}</b> · {d.paciente_nome}</p>
          <p className="text-xs text-ink-muted mt-0.5">{d.itens.length} item(ns) solicitado(s)</p>
        </div>
        <p className="text-xs text-ink-muted">
          A recusa não movimenta estoque. O registro fica marcado como recusado com o motivo;
          a enfermagem vê e pode refazer a solicitação corrigida.
        </p>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">
            Motivo <span className="text-anarosa">(obrigatório)</span>
          </label>
          <input
            className="input mt-1"
            placeholder="ex.: prescrição ilegível; sem saldo na farmácia; item incorreto"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!ok || recusar.isPending} onClick={confirmar}>
            {recusar.isPending ? "Recusando…" : "Confirmar recusa"}
          </button>
        </div>
        {recusar.error ? <p className="text-sm text-anarosa">{(recusar.error as Error).message}</p> : null}
      </div>
    </div>
  );
}

function DispCard({ d }: { d: Dispensacao }) {
  const { data: user } = useUser();
  const [anulando, setAnulando] = useState(false);
  const [recusando, setRecusando] = useState(false);
  const liberar = useLiberarDispensacao();
  const anulada = d.status === "anulada";
  const solicitada = d.status === "solicitada";
  const recusada = d.status === "recusada";
  const ehFarmacia = user && ["admin", "farmaceutico"].includes(user.papel);
  const podeAnular = d.status === "ativa" && ehFarmacia;
  const podeLiberar = solicitada && ehFarmacia;
  const inativa = anulada || recusada;

  return (
    <div className={`card p-4 ${inativa ? "opacity-60" : ""} ${solicitada ? "border-l-4 border-amber-400" : ""}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <b className="font-mono text-sm">{d.numero}</b>
            {anulada ? (
              <span className="badge bg-gray-200 text-gray-700">✕ Anulada</span>
            ) : null}
            {solicitada ? (
              <span className="badge bg-green-100 text-green-800">
                ✔️ Dispensado{ehFarmacia ? " — baixa pendente na farmácia" : ""}
              </span>
            ) : null}
            {recusada ? (
              <span className="badge bg-red-100 text-red-800">✕ Recusada</span>
            ) : null}
            {d.retencao_receita ? (
              <span className="badge bg-amber-100 text-amber-800">📋 Receita retida</span>
            ) : null}
            {d.numero_notificacao_344 ? (
              <span className="badge bg-amber-100 text-amber-800">
                Port. 344 — Notif. {d.numero_notificacao_344}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-ink-muted mt-1">
            Paciente <b>{d.paciente_nome}</b> · CPF {fmtCpf(d.paciente_cpf)} ·
            prontuário {d.paciente_prontuario}
          </p>
          <p className="text-xs text-ink-muted mt-0.5">
            Origem: {d.local_origem_codigo} · dispensado por {d.dispensado_por_nome} em {fmtDateTime(d.created_at)}
          </p>
          {d.medico_prescritor_nome || d.crm_prescritor ? (
            <p className="text-xs text-ink-muted mt-0.5">
              Prescritor: <b>{d.medico_prescritor_nome ?? "—"}</b>
              {d.crm_prescritor ? <> · CRM <b>{d.crm_prescritor}</b></> : null}
            </p>
          ) : null}
          {anulada && d.anulacao_motivo ? (
            <p className="text-xs text-anarosa mt-0.5">
              Anulada{d.anulada_em ? ` em ${fmtDateTime(d.anulada_em)}` : ""}: {d.anulacao_motivo}
            </p>
          ) : null}
          {recusada && d.recusa_motivo ? (
            <p className="text-xs text-anarosa mt-0.5">Recusada: {d.recusa_motivo}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <PrescricaoAnexo d={d} />
          {podeLiberar ? (
            <div className="flex gap-2">
              <button
                className="btn-primary px-2 py-1 text-xs"
                disabled={liberar.isPending}
                onClick={() => liberar.mutate(d.id)}
                title="Conferir e liberar do estoque da farmácia (FEFO), baixando estoque e livro"
              >
                {liberar.isPending ? "Liberando…" : "✓ Liberar"}
              </button>
              <button
                className="btn-secondary px-2 py-1 text-xs"
                onClick={() => setRecusando(true)}
              >
                ✕ Recusar
              </button>
            </div>
          ) : null}
          {podeAnular ? (
            <button
              className="btn-secondary px-2 py-1 text-xs"
              onClick={() => setAnulando(true)}
              title="Anular esta dispensação (reverte estoque e livro; mantém o registro)"
            >
              ✕ Anular
            </button>
          ) : null}
          {liberar.error ? (
            <span className="text-anarosa text-xs">{(liberar.error as Error).message}</span>
          ) : null}
        </div>
      </div>
      {anulando ? <ModalAnular d={d} onClose={() => setAnulando(false)} /> : null}
      {recusando ? <ModalRecusar d={d} onClose={() => setRecusando(false)} /> : null}
      <div className="mt-3 space-y-1.5">
        {d.itens.map((it) => (
          <div
            key={it.id}
            className="flex flex-wrap items-baseline justify-between gap-2 rounded bg-gray-50 px-3 py-1.5 text-sm"
          >
            <span>
              <code className="text-xs">{it.medicamento_codigo}</code> {it.medicamento_descricao}
            </span>
            <span className="text-xs text-ink-muted">
              {it.numero_lote
                ? <>lote <b>{it.numero_lote}</b> · val {it.data_validade ? fmtDate(it.data_validade) : "—"} · </>
                : <span className="text-amber-700">a definir na liberação · </span>}
              qtd <b>{it.quantidade}</b>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Prescrição anexa ----------
function PrescricaoAnexo({ d }: { d: Dispensacao }) {
  const anexar = useAnexarPrescricao();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState(false);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-selecionar o mesmo arquivo
    if (!file) return;
    setErro(null);
    try {
      await anexar.mutateAsync({ dispensacaoId: d.id, file });
    } catch (err) {
      setErro((err as Error).message);
    }
  }

  async function abrir() {
    setErro(null);
    setAbrindo(true);
    try {
      await abrirPrescricao(d.id);
    } catch (err) {
      setErro((err as Error).message);
    } finally {
      setAbrindo(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        className="hidden"
        onChange={onFile}
      />
      {d.prescricao_arquivo_url ? (
        <div className="flex items-center gap-1">
          <button className="btn-secondary px-2 py-1 text-xs" onClick={abrir} disabled={abrindo}>
            {abrindo ? "Abrindo…" : "📎 Ver prescrição"}
          </button>
          <button
            className="btn-secondary px-2 py-1 text-xs" title="Substituir anexo"
            onClick={() => inputRef.current?.click()} disabled={anexar.isPending}
          >
            {anexar.isPending ? "…" : "🔄"}
          </button>
        </div>
      ) : (
        <button
          className="btn-secondary px-2 py-1 text-xs"
          onClick={() => inputRef.current?.click()}
          disabled={anexar.isPending}
        >
          {anexar.isPending ? "Enviando…" : "📎 Anexar prescrição"}
        </button>
      )}
      {erro ? <p className="text-xs text-anarosa max-w-[220px] text-right">{erro}</p> : null}
    </div>
  );
}

// ---------- Nova dispensação ----------
interface ItemRow {
  key: string;
  medicamento_id: string;
  quantidade: string;
}

function NovaDispensacao({ onDone }: { onDone: () => void }) {
  const { data: user } = useUser();
  const { data: locais } = useLocais();
  const { data: medicamentos } = useMedicamentos({});
  const criar = useCriarDispensacao();
  const criarEnf = useCriarDispensacaoEnfermagem();

  const enf = user?.papel === "enfermagem" || user?.papel === "enfermeira_chefe";

  // Locais que o usuário pode usar como origem. Enfermagem escolhe o posto
  // (o dela vem pré-selecionado); a farmácia é para itens em falta no posto.
  const locaisOrigem = useMemo(() => {
    if (!locais) return [];
    if (enf) return locais.filter((l) => l.tipo === "enfermagem");
    return locais.filter((l) => l.tipo === "farmacia" || l.tipo === "enfermagem");
  }, [locais, enf]);
  const farmacias = useMemo(
    () => (locais ?? []).filter((l) => l.tipo === "farmacia"),
    [locais],
  );

  const [farmacia, setFarmacia] = useState("");
  useEffect(() => {
    if (!farmacia && farmacias.length) setFarmacia(farmacias[0].id);
  }, [farmacia, farmacias]);

  const [origem, setOrigem] = useState("");
  useEffect(() => {
    if (origem || locaisOrigem.length === 0) return;
    const meuLocal = locaisOrigem.find((l) => l.id === user?.local_id);
    setOrigem((meuLocal ?? locaisOrigem[0]).id);
  }, [origem, locaisOrigem, user]);
  const [busca, setBusca] = useState("");
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [medicoNome, setMedicoNome] = useState("");
  const [crm, setCrm] = useState("");
  const [notif344, setNotif344] = useState("");
  const [itens, setItens] = useState<ItemRow[]>([
    { key: crypto.randomUUID(), medicamento_id: "", quantidade: "1" },
  ]);

  // OCR de prescrição: lê a foto e PRÉ-PREENCHE (a enfermeira confere e envia).
  const lerPresc = useLerPrescricao();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [ocrMsg, setOcrMsg] = useState<string | null>(null);
  const [camOpen, setCamOpen] = useState(false);

  async function handleOcr(file: File) {
    setOcrMsg(null);
    try {
      const r = await lerPresc.mutateAsync(file);
      if (r.cpf) setBusca(r.cpf);
      if (r.medico) setMedicoNome(r.medico);
      if (r.crm) setCrm(r.crm);
      const vistos = new Set<string>();
      const linhas: ItemRow[] = [];
      for (const c of r.medicamentos) {
        if (vistos.has(c.medicamento_id)) continue;
        vistos.add(c.medicamento_id);
        linhas.push({ key: crypto.randomUUID(), medicamento_id: c.medicamento_id, quantidade: "1" });
        if (linhas.length >= 8) break;
      }
      if (linhas.length) setItens(linhas);
      const partes: string[] = [];
      if (linhas.length) partes.push(`${linhas.length} medicamento(s) sugerido(s)`);
      if (r.cpf) partes.push("CPF do paciente");
      if (r.medico || r.crm) partes.push("médico/CRM");
      setOcrMsg(partes.length
        ? `🔵 Lido: ${partes.join(" · ")}. Confira cada campo, o medicamento certo e a quantidade antes de enviar.`
        : "Não consegui extrair dados legíveis (prescrição manuscrita?). Preencha manualmente.");
    } catch (e) {
      setOcrMsg((e as Error).message);
    }
  }

  // Detecta se há item alto custo ou Port. 344 nos itens escolhidos
  const itensMeds = itens
    .map((i) => medicamentos?.find((m) => m.id === i.medicamento_id))
    .filter((m): m is Medicamento => !!m);
  const requerPrescritor = itensMeds.some(
    (m) => m.categoria === "alto_custo" || m.portaria_344,
  );
  const requerNotif = itensMeds.some(
    (m) => m.portaria_344 && m.tipo_receituario_344 != null && RECEITUARIOS_RETIDOS.has(m.tipo_receituario_344),
  );

  function update(key: string, patch: Partial<ItemRow>) {
    setItens((arr) => arr.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  }
  function add() {
    setItens((arr) => [...arr, { key: crypto.randomUUID(), medicamento_id: "", quantidade: "1" }]);
  }
  function remove(key: string) {
    setItens((arr) => arr.filter((i) => i.key !== key));
  }

  const itensLimpos = itens
    .filter((i) => i.medicamento_id && Number(i.quantidade) > 0)
    .map((i) => ({ medicamento_id: i.medicamento_id, quantidade: Number(i.quantidade) }));
  const presc = {
    medico_prescritor_nome: requerPrescritor ? medicoNome : undefined,
    crm_prescritor: requerPrescritor ? crm : undefined,
    numero_notificacao_344: requerNotif ? notif344 : undefined,
    retencao_receita: requerNotif,
  };

  async function submit() {
    if (!paciente || !origem || itensLimpos.length === 0) return;
    try {
      if (enf) {
        await criarEnf.mutateAsync({
          posto_id: origem,
          paciente_id: paciente.id,
          itens: itensLimpos,
          farmacia_solicitada_id: farmacia || null,
          ...presc,
        });
      } else {
        await criar.mutateAsync({ local_origem_id: origem, paciente_id: paciente.id, itens: itensLimpos, ...presc });
      }
      onDone();
    } catch {
      // erro exibido via mutation.error
    }
  }

  const mut = enf ? criarEnf : criar;

  return (
    <div className="card p-4 space-y-4">
      <h3 className="font-display text-base font-semibold">Nova dispensação</h3>

      {enf || user?.papel === "admin" ? (
        <div className="rounded-md border border-anarosa-100 bg-anarosa-50 p-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleOcr(f); e.target.value = ""; }}
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={lerPresc.isPending}
            onClick={() => setCamOpen(true)}
          >
            {lerPresc.isPending ? "Lendo prescrição…" : "📷 Tirar foto da prescrição"}
          </button>
          <button
            type="button"
            className="ml-3 text-xs text-ink-muted hover:underline"
            disabled={lerPresc.isPending}
            onClick={() => fileRef.current?.click()}
          >
            ou enviar arquivo (foto/PDF)
          </button>
          {ocrMsg ? <p className="mt-2 text-xs text-ink">{ocrMsg}</p> : (
            <p className="mt-2 text-xs text-ink-muted">
              Tire a foto da prescrição — a IA preenche paciente, médico e medicamentos
              pra você <b>conferir</b> e enviar. Nada é enviado sem sua confirmação.
            </p>
          )}
          <PrescricaoCamera
            open={camOpen}
            onClose={() => setCamOpen(false)}
            onCapture={(f) => { setCamOpen(false); handleOcr(f); }}
          />
        </div>
      ) : null}

      <div className={enf ? "grid grid-cols-1 gap-3 md:grid-cols-2" : ""}>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">
            {enf ? "Posto (seu estoque)" : "Origem"}
          </label>
          <select
            className="input mt-1"
            value={origem}
            onChange={(e) => setOrigem(e.target.value)}
            disabled={locaisOrigem.length === 1}
          >
            {locaisOrigem.map((l) => (
              <option key={l.id} value={l.id}>{l.descricao}</option>
            ))}
          </select>
        </div>
        {enf ? (
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">
              Farmácia (p/ itens em falta no posto)
            </label>
            <select className="input mt-1" value={farmacia} onChange={(e) => setFarmacia(e.target.value)}>
              {farmacias.map((l) => (
                <option key={l.id} value={l.id}>{l.descricao}</option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
      {enf ? (
        <p className="text-xs text-ink-muted -mt-2">
          O que houver no posto sai na hora; o que faltar vira <b>solicitação</b> à farmácia,
          que confere e libera.
        </p>
      ) : null}

      <PacienteSelect
        busca={busca}
        onBuscaChange={setBusca}
        paciente={paciente}
        onSelect={setPaciente}
      />

      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Itens</label>
        <div className="mt-1 space-y-2">
          {itens.map((row) => (
            <ItemRowEdit
              key={row.key}
              row={row}
              origem={origem}
              medicamentos={medicamentos ?? []}
              onChange={(p) => update(row.key, p)}
              onRemove={itens.length > 1 ? () => remove(row.key) : undefined}
            />
          ))}
          <button className="text-anarosa text-sm hover:underline" onClick={add}>
            + Adicionar item
          </button>
        </div>
      </div>

      {requerPrescritor ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="text-xs uppercase tracking-wider text-amber-800 font-medium">
            ⚠️ Item alto custo ou Portaria 344 — prescritor obrigatório
          </p>
          <MedicoSelect
            nome={medicoNome}
            crm={crm}
            onChange={(n, c) => { setMedicoNome(n); setCrm(c); }}
          />
          {requerNotif ? (
            <div>
              <p className="text-xs text-amber-800">
                Receituário A/B (Port. 344) — número de notificação obrigatório, receita retida
              </p>
              <input
                className="input mt-1"
                placeholder="Número da notificação"
                value={notif344}
                onChange={(e) => setNotif344(e.target.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <button
          className="btn-primary"
          disabled={
            !paciente || !origem || itensLimpos.length === 0 || mut.isPending ||
            (requerPrescritor && (!medicoNome || !crm)) ||
            (requerNotif && !notif344)
          }
          onClick={submit}
        >
          {mut.isPending ? "Salvando…" : enf ? "Confirmar / solicitar" : "Confirmar dispensação"}
        </button>
        {mut.error ? (
          <span className="text-anarosa text-sm">{(mut.error as Error).message}</span>
        ) : null}
      </div>
    </div>
  );
}

function PacienteSelect({
  busca, onBuscaChange, paciente, onSelect,
}: {
  busca: string;
  onBuscaChange: (s: string) => void;
  paciente: Paciente | null;
  onSelect: (p: Paciente | null) => void;
}) {
  const { data, isLoading } = usePacientes(busca);
  const [cadastrando, setCadastrando] = useState(false);
  if (paciente) {
    return (
      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Paciente</label>
        <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          <span>
            <b>{paciente.nome}</b> · CPF {fmtCpf(paciente.cpf)} · prontuário {paciente.numero_prontuario}
          </span>
          <button className="text-sm text-anarosa hover:underline" onClick={() => onSelect(null)}>
            trocar
          </button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-ink-muted">Buscar paciente</label>
      <input
        className="input mt-1"
        placeholder="CPF, prontuário ou nome (3+ caracteres)…"
        value={busca}
        onChange={(e) => onBuscaChange(e.target.value)}
      />
      {busca.length < 3 ? null : isLoading ? (
        <p className="text-xs text-ink-muted mt-1">Buscando…</p>
      ) : data && data.length > 0 ? (
        <ul className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200">
          {data.map((p) => (
            <li
              key={p.id}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-anarosa-50"
              onClick={() => onSelect(p)}
            >
              <b>{p.nome}</b>{" "}
              <span className="text-ink-muted text-xs">
                · CPF {fmtCpf(p.cpf)} · prontuário {p.numero_prontuario}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-muted mt-1">Nenhum paciente encontrado.</p>
      )}
      <button
        type="button"
        className="mt-2 text-sm text-anarosa hover:underline"
        onClick={() => setCadastrando(true)}
      >
        + Cadastrar novo paciente
      </button>
      {cadastrando ? (
        <PacienteFormModal
          nomeInicial={/^[\d.\-]+$/.test(busca) ? "" : busca}
          onCreated={(p) => { setCadastrando(false); onSelect(p); }}
          onClose={() => setCadastrando(false)}
        />
      ) : null}
    </div>
  );
}

function fmtCrm(m: Medico): string {
  return m.uf_crm ? `${m.uf_crm}-${m.crm}` : m.crm;
}

function MedicoSelect({
  nome, crm, onChange,
}: {
  nome: string;
  crm: string;
  onChange: (nome: string, crm: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [manual, setManual] = useState(false);
  const { data, isLoading } = useMedicos(query);
  const criar = useCriarMedico();

  // Médico já definido (por seleção, OCR ou digitação) → mostra confirmado.
  if (nome && !manual) {
    return (
      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Médico prescritor</label>
        <div className="mt-1 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
          <span>
            <b>{nome}</b>{crm ? <> · CRM <b>{crm}</b></> : <span className="text-anarosa"> · sem CRM</span>}
          </span>
          <button
            type="button"
            className="text-sm text-anarosa hover:underline"
            onClick={() => { onChange("", ""); setQuery(""); }}
          >
            trocar
          </button>
        </div>
      </div>
    );
  }

  if (manual) {
    return (
      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Médico prescritor (manual)</label>
        <div className="mt-1 grid grid-cols-1 gap-2 md:grid-cols-2">
          <input
            className="input"
            placeholder="Nome do médico"
            value={nome}
            onChange={(e) => onChange(e.target.value, crm)}
          />
          <input
            className="input"
            placeholder="CRM (ex: SP-12345)"
            value={crm}
            onChange={(e) => onChange(nome, e.target.value)}
          />
        </div>
        <div className="mt-2 flex items-center gap-3">
          <button type="button" className="text-sm text-ink-muted hover:underline" onClick={() => setManual(false)}>
            ← buscar no corpo clínico
          </button>
          {nome && crm ? (
            <button
              type="button"
              className="text-sm text-anarosa hover:underline disabled:opacity-50"
              disabled={criar.isPending}
              onClick={async () => {
                const [uf, ...resto] = crm.includes("-") ? crm.split("-") : ["", crm];
                try {
                  await criar.mutateAsync({
                    nome,
                    crm: (resto.join("-") || crm).trim(),
                    uf_crm: uf.length === 2 ? uf.toUpperCase() : null,
                    origem: "manual",
                  });
                } catch { /* 409 duplicado é ok */ }
              }}
            >
              {criar.isPending ? "salvando…" : "+ salvar no corpo clínico"}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-ink-muted">Buscar médico (corpo clínico)</label>
      <input
        className="input mt-1"
        placeholder="Nome ou CRM (2+ caracteres)…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {query.length < 2 ? null : isLoading ? (
        <p className="text-xs text-ink-muted mt-1">Buscando…</p>
      ) : data && data.length > 0 ? (
        <ul className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200">
          {data.map((m) => (
            <li
              key={m.id}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-anarosa-50"
              onClick={() => onChange(m.nome, fmtCrm(m))}
            >
              <b>{m.nome}</b>{" "}
              <span className="text-ink-muted text-xs">
                · CRM {fmtCrm(m)}{m.especialidade ? ` · ${m.especialidade}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-muted mt-1">Nenhum médico encontrado no corpo clínico.</p>
      )}
      <button
        type="button"
        className="mt-2 text-sm text-anarosa hover:underline"
        onClick={() => { setManual(true); if (!nome && query) onChange(query, crm); }}
      >
        + Não está na lista — digitar manualmente
      </button>
    </div>
  );
}

function ItemRowEdit({
  row, origem, medicamentos, onChange, onRemove,
}: {
  row: ItemRow;
  origem: string;
  medicamentos: Medicamento[];
  onChange: (p: Partial<ItemRow>) => void;
  onRemove?: () => void;
}) {
  const fefo = useFefoSugestao(row.medicamento_id || null, origem || null);
  const med = medicamentos.find((m) => m.id === row.medicamento_id);
  const altoCusto = med?.categoria === "alto_custo";
  const p344 = med?.portaria_344;
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanErr, setScanErr] = useState<string | null>(null);

  async function handleScan(codigo: string) {
    setScannerOpen(false);
    setScanErr(null);
    // Tenta primeiro etiqueta interna (datamatrix do lote)
    try {
      const lote = await lookupLotePorEtiqueta(codigo);
      onChange({ medicamento_id: lote.medicamento_id });
      return;
    } catch {
      // não é etiqueta interna; tenta como EAN
    }
    try {
      const m = await lookupMedicamentoPorEan(codigo);
      onChange({ medicamento_id: m.id });
    } catch {
      setScanErr(`Código "${codigo}" não corresponde a EAN nem etiqueta interna.`);
    }
  }

  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_100px_auto_auto]">
        <MedicamentoCombobox
          medicamentos={medicamentos}
          value={row.medicamento_id}
          onChange={(id) => onChange({ medicamento_id: id })}
        />
        <input
          className="input"
          type="number"
          min={1}
          value={row.quantidade}
          onChange={(e) => onChange({ quantidade: e.target.value })}
        />
        <button
          type="button"
          className="btn-secondary"
          title="Escanear código de barras / etiqueta"
          onClick={() => setScannerOpen(true)}
        >
          📷
        </button>
        {onRemove ? (
          <button className="text-ink-muted text-sm hover:text-anarosa" onClick={onRemove}>
            Remover
          </button>
        ) : <div />}
      </div>
      <Scanner open={scannerOpen} onScan={handleScan} onClose={() => setScannerOpen(false)} />
      {scanErr ? <p className="mt-1 text-xs text-anarosa">{scanErr}</p> : null}
      {altoCusto || p344 ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {altoCusto ? <span className="badge-alto-custo">alto custo</span> : null}
          {p344 ? <span className="badge bg-amber-100 text-amber-800">Port. 344</span> : null}
        </div>
      ) : null}
      {row.medicamento_id && origem ? (
        <p className="mt-1.5 text-xs text-ink-muted">
          {fefo.isLoading ? "Buscando FEFO…" :
            fefo.isError ? "⚠️ Sem lote ativo no local de origem." :
            fefo.data ? <>FEFO: lote <b>{fefo.data.numero_lote}</b> · val {fmtDate(fefo.data.data_validade)} · disponível: <b>{fefo.data.saldo_disponivel}</b></> :
            null}
        </p>
      ) : null}
    </div>
  );
}
